import { useEffect, useRef } from 'react';

export default function CircularEconomy() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const circularSteps = [
    {
      number: '01',
      title: '陸上養殖',
      description: '閉鎖循環型システムで魚を養殖',
      icon: 'ri-drop-line'
    },
    {
      number: '02',
      title: '有機廃棄物',
      description: '養殖過程で発生する有機物を回収',
      icon: 'ri-recycle-line'
    },
    {
      number: '03',
      title: '肥料化',
      description: '有機廃棄物を高品質な肥料に変換',
      icon: 'ri-seedling-line'
    },
    {
      number: '04',
      title: '農業利用',
      description: '地域農業で肥料として活用',
      icon: 'ri-plant-line'
    },
    {
      number: '05',
      title: '農作物生産',
      description: '持続可能な農作物を生産',
      icon: 'ri-leaf-line'
    },
    {
      number: '06',
      title: '地域循環',
      description: '地域内で資源を循環させる',
      icon: 'ri-refresh-line'
    }
  ];

  return (
    <section ref={sectionRef} className="py-24 bg-gradient-to-b from-emerald-50/30 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 animate-on-scroll opacity-0 translate-y-8 transition-all duration-1000">
          <div className="inline-block px-4 py-2 bg-emerald-100 rounded-full mb-4">
            <span className="text-sm font-semibold text-emerald-700 tracking-wide">CIRCULAR ECONOMY</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">循環型社会への貢献</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            廃棄物を資源として活用し、地域内で資源を循環させる<br />持続可能な循環型経済モデルを構築しています。
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {circularSteps.map((step, index) => (
            <div
              key={index}
              className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-1000 bg-white rounded-2xl p-8 border-2 border-emerald-100 hover:border-emerald-300 hover:shadow-lg transition-all duration-500 relative"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="absolute -top-4 -right-4 w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                {step.number}
              </div>
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mb-6">
                <i className={`${step.icon} text-3xl text-emerald-600`}></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
              <p className="text-gray-600 leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-8 animate-on-scroll opacity-0 translate-y-8 transition-all duration-1000">
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-10 text-white">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6">
              <i className="ri-loop-right-line text-3xl text-white"></i>
            </div>
            <h3 className="text-3xl font-bold mb-4">資源循環の実現</h3>
            <p className="text-white/90 leading-relaxed mb-6">
              養殖事業から発生する有機廃棄物を地域の農業資源として活用することで、廃棄物ゼロを目指した循環型システムを実現しています。
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <div className="text-3xl font-bold mb-1">100%</div>
                <div className="text-sm text-white/80">有機廃棄物再利用率</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <div className="text-3xl font-bold mb-1">450t</div>
                <div className="text-sm text-white/80">年間肥料生産量</div>
              </div>
            </div>
          </div>

          <div className="relative h-full min-h-[400px] rounded-2xl overflow-hidden">
            <img
              src="https://public.readdy.ai/ai/img_res/03c9df7dc33dfa5b1a884fb9b3b10fae.jpg"
              alt="循環型経済"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
          </div>
        </div>

        <div className="mt-16 animate-on-scroll opacity-0 translate-y-8 transition-all duration-1000 bg-white rounded-2xl p-10 shadow-lg border border-emerald-100">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">地域との連携による循環型モデル</h3>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-building-line text-3xl text-emerald-600"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">C.GREEN</h4>
              <p className="text-sm text-gray-600">陸上養殖事業</p>
            </div>
            <div className="text-center flex items-center justify-center">
              <i className="ri-arrow-down-line md:hidden text-3xl text-emerald-500"></i>
              <i className="ri-arrow-right-line hidden md:block text-3xl text-emerald-500"></i>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-community-line text-3xl text-emerald-600"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">地域農家</h4>
              <p className="text-sm text-gray-600">肥料活用</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}